import React, { useState, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert, FormGroup, Label, Input } from 'reactstrap';
import { adalApiFetch } from './adalConfig';

const moment = require('moment');
moment.locale('it-IT');

function capitalize(mySentence) {
  const words = mySentence.split(" ");
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i][0] ? words[i][0].toUpperCase() + words[i].substr(1) : words[i];
  }
  return words.join(" ");
}

function nameFormatter(value) {
  return value ? capitalize(value) : '';
}

function dateFormatter(value) {
  return value ? moment(value).format("DD/MM/YYYY") : '';
}

const RichiesteContatto = (props) => {
  const [richieste, setRichieste] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchko, setFetchko] = useState(null);
  const [modal, setModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [esito, setEsito] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI + '/requests/richiestecontatto')
        .then(result => result.json())
        .then(richieste => {
            const formattedRichieste = richieste.map(richiesta => ({
            id: richiesta.id_richiestacontatto,
            stato: richiesta.stato == 'A' ? 'Aperta' : 'Chiusa', 
            telefono: richiesta.Associato.telefono,
            motivo: richiesta.motivo,
            cognome: richiesta.Associato.cognome,
            nome: richiesta.Associato.nome,
            email: richiesta.Associato.email,
            data_richiesta: new Date(richiesta.data_richiesta),
            note: richiesta.note,
            esito: richiesta.esito,
            }));
            setRichieste(formattedRichieste);
            setLoading(false);
        });
    }

  const handleCloseRequest = (id) => {
    setSelectedRequestId(id);
    setEsito('');
    setError(null);
    setModal(true);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/requests/closerichiestacontatto`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id_richiestacontatto: selectedRequestId, esito }),
    }).then(response => {
      if (response.ok) {
        setModal(false);
        fetchData();
        // Aggiorna il dato dell'header
        props.updateHeaderData();
      } else {
        return response.json().then(errorData => {
          setError(`Errore durante la chiusura della richiesta. ${errorData.error}`);
        });
      }
    }).catch((error) => {
      setError(`Errore durante la chiusura della richiesta. ${error.message}`);
    }).finally(() => {
      setIsSubmitting(false);
    });
  };

  const processRowUpdate = async (newRow, oldRow) => {
    // Chiamata API per aggiornare la nota
    try {
      await adalApiFetch(fetch, `${process.env.REACT_APP_BASE_SERVER_URI}/requests/updatenote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_richiestacontatto: newRow.id, note: newRow.note }),
      });
      // Aggiorna lo stato locale
      setRichieste((prev) =>
        prev.map((row) => (row.id === newRow.id ? { ...row, note: newRow.note } : row))
      );
      return newRow;
    } catch (error) {
      setFetchko('Errore durante il salvataggio della nota.');
      return oldRow;
    }
  };

  const columns = [
    {
      field: 'cognome',
      headerName: 'Cognome',
      width: 150,
      sortable: true,
      valueFormatter: nameFormatter,
    },
    {
      field: 'nome',
      headerName: 'Nome',
      width: 150,
      sortable: true,
      valueFormatter: nameFormatter,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      sortable: true,
    },
    {
        field: 'telefono',
        headerName: 'Telefono',
        width: 140,
        sortable: true,
    },
    {
      field: 'motivo',
      headerName: 'Motivo',
      width: 140,
      sortable: true,
      valueFormatter: (value) => value ? value.toUpperCase() : '',
    },
    {
      field: 'data_richiesta',
      headerName: 'Data richiesta',
      width: 150,
      sortable: true,
      valueFormatter: dateFormatter,
    },
    {
      field: 'stato',
      headerName: 'Stato',
      width: 80,
      sortable: false,
    },
    {
      field: 'esito',
      headerName: 'Esito',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        params.row.stato === 'Aperta' ? (
          <Button color="primary" onClick={() => handleCloseRequest(params.row.id)}>
            Chiudi richiesta
          </Button>
        ) : (
          params.value
        )
      ),
    },
    {
      field: 'note',
      headerName: 'Note',
      width: 500,
      sortable: false,
       editable: true, // Rende la cella modificabile
    },
  ];

  return (
    loading ?
    <Box
      sx={{
        display: "flex",
        justifyContent: "center", // Centra orizzontalmente
        alignItems: "flex-start", // Allinea in alto
        height: "100vh", // Altezza della pagina intera
        paddingTop: "40px", // Spaziatura dal bordo superiore (regolabile)
        backgroundColor: "background.default", // Facoltativo, per uno sfondo uniforme
      }}
    >
      <CircularProgress size="30px" />
    </Box>
    :
    <Container fluid>
      <Alert color="danger" isOpen={fetchko !== null}>
          {fetchko}
      </Alert>
      <DataGrid
        rows={richieste}
        columns={columns}
        processRowUpdate={processRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
          filter: {
            filterModel: {
              items: [
                { field: 'stato', operatorValue: 'equals', value: 'Aperta' },
              ],
            },
          },
        }}
        components={{
          Toolbar: GridToolbar
        }}
        pageSize={10}
        pageSizeOptions={[10, 20]}
        />
      <Modal isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(!modal)}>Chiusura richiesta contatto</ModalHeader>
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}
          <FormGroup>
            <Label for="esito">Esito</Label>
            <Input
              type="text"
              name="esito"
              id="esito"
              value={esito}
              onChange={(e) => setEsito(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>Invia</Button>{' '}
          <Button color="secondary" onClick={() => setModal(!modal)}>Annulla</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default RichiesteContatto;